import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useFloating, autoUpdate, offset, flip, shift, size } from '@floating-ui/react';
import { ChevronDown } from 'lucide-react';

const PortalDropdown = ({
    name,
    options = [],
    value,
    onChange,
    placeholder = 'Select option',
    className = '',
    disabled = false,
}) => {
    const [isOpen, setIsOpen] = useState(false);

    const { refs, floatingStyles, update } = useFloating({
        open: isOpen,
        onOpenChange: setIsOpen,
        middleware: [
            offset(4),
            flip(),
            shift(),
            size({
                apply({ rects, elements }) {
                    // Set min-width to match reference, but allow it to grow
                    Object.assign(elements.floating.style, {
                        minWidth: `${rects.reference.width}px`,
                        width: 'max-content',
                    });
                },
                padding: 8, // Add some padding for safety
            }),
        ],
        whileElementsMounted: autoUpdate,
    });

    const containerRef = useRef(null);
    const portalRef = useRef(null);

    // Close on outside click
    useEffect(() => {
        const handleClickOutside = (e) => {
            const isInsideContainer = containerRef.current?.contains(e.target);
            const isInsidePortal = portalRef.current?.contains(e.target);
            if (!isInsideContainer && !isInsidePortal) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleSelect = (option) => {
        const optionValue = typeof option === 'object' ? option.value : option;
        onChange(name, optionValue);
        setIsOpen(false);
    };

    const getDisplayLabel = () => {
        if (!value) return placeholder;
        const option = options.find(opt => {
            const optValue = typeof opt === 'object' ? opt.value : opt;
            return optValue === value;
        });
        if (option) {
            return typeof option === 'object' ? option.label : option;
        }
        return value;
    };

    const renderDropdown = () => {
        if (!isOpen) return null;

        return createPortal(
            <div
                ref={(el) => {
                    refs.setFloating(el);
                    portalRef.current = el;
                }}
                className="bg-white border border-gray-200 rounded-lg shadow-lg z-[9999] overflow-y-auto"
                style={{
                    ...floatingStyles,
                    maxHeight: '200px',
                }}
            >
                {options.length === 0 ? (
                    <div className="px-3 py-2 text-gray-400 text-sm">No options</div>
                ) : (
                    options.map((opt, index) => {
                        const optValue = typeof opt === 'object' ? opt.value : opt;
                        const optLabel = typeof opt === 'object' ? opt.label : opt;
                        const isSelected = value === optValue;
                        return (
                            <div
                                key={index}
                                className={`px-3 py-2 cursor-pointer hover:bg-gray-50 text-sm ${isSelected ? 'bg-blue-50 text-blue-600' : 'text-gray-700'
                                    }`}
                                onClick={() => handleSelect(opt)}
                            >
                                {optLabel}
                            </div>
                        );
                    })
                )}
            </div>,
            document.body
        );
    };

    return (
        <div ref={containerRef} className={`relative ${className}`}>
            <button
                ref={refs.setReference}
                type="button"
                disabled={disabled}
                onClick={() => !disabled && setIsOpen(!isOpen)}
                className={`w-full flex items-center justify-between px-3 py-2 border border-gray-300 rounded-lg bg-white text-left text-sm focus:outline-none focus:ring-2 focus:ring-pink-500 ${disabled ? 'opacity-60 cursor-not-allowed' : 'hover:border-gray-400'
                    }`}
            >
                <span className={`truncate ${!value ? 'text-gray-400' : 'text-gray-700'}`}>
                    {getDisplayLabel()}
                </span>
                <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''
                    }`} />
            </button>
            {renderDropdown()}
        </div>
    );
};

export default PortalDropdown;